import { useRef, useState, type ReactNode } from 'react';
import { usePrefsValue } from '@adiox/ui';
import { ContextMenu, type MenuItem } from '../../chart/ChartOverlays';
import { useChartCommands } from '../../chart/chartCommands';
import { STANDARD_TIMEFRAMES, TIMEFRAMES } from '../../chart/chartModel';
import { normalizeTemplates } from '../../chart/templates';
import { TOOL_LABELS, type Tool } from '../../drawings/model';
import { INDICATORS } from '../../indicators/library';
import { useSymbols } from '../../market/useSymbols';
import { useWorkspace } from '../../workspace/workspaceStore';
import { normalizeProfiles } from '../profiles';
import { ClassicIcon, type ClassicIconName } from './icons';
import type { ClassicActions } from './MenuBar';

type Menu = { x: number; y: number; title: string; items: MenuItem[]; searchable?: boolean } | null;

function Button({
  icon,
  label,
  shortcut,
  pressed,
  disabled,
  menu,
  text,
  onClick,
}: {
  icon?: ClassicIconName;
  label: string;
  shortcut?: string;
  pressed?: boolean;
  disabled?: boolean;
  /** Opens a drop-down (drawn with a small arrow). */
  menu?: boolean;
  /** Text next to (or instead of) the icon. */
  text?: string;
  onClick: (box: DOMRect) => void;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      title={shortcut ? `${label} (${shortcut})` : label}
      aria-pressed={menu ? undefined : pressed}
      aria-haspopup={menu ? 'menu' : undefined}
      disabled={disabled}
      onClick={() => onClick(ref.current!.getBoundingClientRect())}
      className={`classic-tool ${pressed ? 'is-pressed' : ''}`}
    >
      {icon && <ClassicIcon name={icon} />}
      {text && <span>{text}</span>}
      {menu && (
        <svg width="7" height="4" viewBox="0 0 7 4" aria-hidden="true">
          <path d="M0 0h7L3.5 4z" fill="currentColor" />
        </svg>
      )}
    </button>
  );
}

const Sep = () => <span className="classic-sep" aria-hidden="true" />;

const LINE_TOOLS: { tool: Tool; icon: ClassicIconName }[] = [
  { tool: 'vline', icon: 'vline' },
  { tool: 'hline', icon: 'hline' },
  { tool: 'trend', icon: 'trend' },
  { tool: 'channel', icon: 'channel' },
  { tool: 'fib', icon: 'fib' },
  { tool: 'text', icon: 'text' },
  { tool: 'priceLabel', icon: 'label' },
];
const SHAPES: Tool[] = ['rect', 'triangle', 'ellipse'];
const MORE_TOOLS: Tool[] = [
  'ray',
  'regression',
  'stddev',
  'pitchfork',
  'fibExpansion',
  'fibFan',
  'fibArcs',
  'fibTimeZones',
  'gannLine',
  'gannFan',
  'gannGrid',
  'elliottImpulse',
  'elliottCorrection',
];

/**
 * The main toolbar of the classic workspace: new chart and profiles, the windows, new order, chart type, zoom, scrolling,
 * indicators, templates and periods, the cursor and the drawing tools, the timeframes, a screenshot and symbol search. Chart
 * buttons act on the active chart.
 */
export function MainToolbar({ actions }: { actions: ClassicActions }) {
  const chart = useChartCommands((s) => s.active);
  const s = chart?.state;
  const [menu, setMenu] = useState<Menu>(null);
  const [search, setSearch] = useState('');
  const symbols = useSymbols().data;
  const templates = normalizeTemplates(usePrefsValue((st) => st.data.chartTemplates));
  const profiles = normalizeProfiles(usePrefsValue((st) => st.data.profiles));
  const panels = actions.listPanels();
  const isOpen = (id: string) => panels.find((p) => p.id === id)?.open ?? false;
  const at = (box: DOMRect) => ({ x: box.left, y: box.bottom + 2 });
  const no = !chart;

  const pick = (tool: Tool | 'measure' | null) => chart?.setTool(s?.tool === tool ? null : tool);
  const findSymbol = () => {
    const q = search.trim().toUpperCase();
    if (!q) return;
    const match =
      symbols?.find((x) => x.name.toUpperCase() === q) ??
      symbols?.find((x) => x.name.toUpperCase().startsWith(q)) ??
      symbols?.find((x) => x.description.toUpperCase().includes(q));
    if (!match) return;
    useWorkspace.getState().select(match.name);
    chart?.setSymbol(match.name);
    setSearch('');
  };

  const group = (children: ReactNode) => <div className="flex items-center">{children}</div>;

  return (
    <div
      role="toolbar"
      aria-label="Main toolbar"
      className="classic-toolbar flex shrink-0 items-center"
      data-testid="main-toolbar"
    >
      {group(
        <>
          <Button icon="newChart" label="New Chart" onClick={actions.newChart} />
          <Button
            icon="profiles"
            label="Profiles"
            menu
            onClick={(box) =>
              setMenu({
                ...at(box),
                title: 'Profiles',
                items: [
                  ...Object.keys(profiles).map((name) => ({
                    key: name,
                    label: name,
                    onSelect: () => useWorkspace.getState().actions?.applyProfile?.(name),
                  })),
                  { key: 'manage', label: 'Save or delete profiles…', onSelect: actions.openProfiles },
                ],
              })
            }
          />
        </>,
      )}
      <Sep />
      {group(
        <>
          <Button
            icon="marketWatch"
            label="Market Watch"
            shortcut="Ctrl+M"
            pressed={isOpen('marketWatch')}
            onClick={() => actions.togglePanel('marketWatch')}
          />
          <Button
            icon="dataWindow"
            label="Data Window"
            shortcut="Ctrl+D"
            pressed={isOpen('dataWindow')}
            onClick={() => actions.togglePanel('dataWindow')}
          />
          <Button
            icon="navigator"
            label="Navigator"
            pressed={isOpen('navigator')}
            onClick={() => actions.togglePanel('navigator')}
          />
          <Button
            icon="toolbox"
            label="Toolbox"
            pressed={actions.toolboxOpen()}
            onClick={actions.toggleToolbox}
          />
        </>,
      )}
      <Sep />
      <Button icon="newOrder" label="New Order" text="New Order" shortcut="F9" onClick={actions.newOrder} />
      <Sep />
      {group(
        <>
          <Button
            icon="bars"
            label="Bar Chart"
            shortcut="Alt+1"
            disabled={no}
            pressed={s?.type === 'bars'}
            onClick={() => chart?.setType('bars')}
          />
          <Button
            icon="candles"
            label="Candlesticks"
            shortcut="Alt+2"
            disabled={no}
            pressed={s?.type === 'candles'}
            onClick={() => chart?.setType('candles')}
          />
          <Button
            icon="line"
            label="Line Chart"
            shortcut="Alt+3"
            disabled={no}
            pressed={s?.type === 'line'}
            onClick={() => chart?.setType('line')}
          />
        </>,
      )}
      <Sep />
      {group(
        <>
          <Button icon="zoomIn" label="Zoom In" shortcut="+" disabled={no} onClick={() => chart?.zoom(1)} />
          <Button
            icon="zoomOut"
            label="Zoom Out"
            shortcut="−"
            disabled={no}
            onClick={() => chart?.zoom(-1)}
          />
        </>,
      )}
      <Sep />
      {group(
        <>
          <Button
            icon="autoScroll"
            label="Auto Scroll"
            disabled={no}
            pressed={s?.autoScroll}
            onClick={() => chart?.toggle('autoScroll')}
          />
          <Button
            icon="chartShift"
            label="Chart Shift"
            disabled={no}
            pressed={s?.chartShift}
            onClick={() => chart?.toggle('chartShift')}
          />
        </>,
      )}
      <Sep />
      {group(
        <>
          <Button
            icon="indicators"
            label="Indicators"
            menu
            disabled={no}
            onClick={(box) =>
              setMenu({
                ...at(box),
                title: 'Indicators',
                searchable: true,
                items: INDICATORS.map((d) => ({
                  key: d.id,
                  label: `${d.group}: ${d.name}`,
                  onSelect: () => chart?.addIndicator(d.id),
                })),
              })
            }
          />
          <Button
            icon="templates"
            label="Templates"
            menu
            disabled={no}
            onClick={(box) =>
              setMenu({
                ...at(box),
                title: 'Templates',
                items: [
                  { key: 'save', label: 'Save Template…', onSelect: () => chart?.saveTemplate() },
                  ...Object.keys(templates).map((name) => ({
                    key: name,
                    label: name,
                    onSelect: () => chart?.applyTemplate(name),
                  })),
                ],
              })
            }
          />
          <Button
            icon="periods"
            label="Periods"
            menu
            disabled={no}
            onClick={(box) =>
              setMenu({
                ...at(box),
                title: 'Periods',
                items: TIMEFRAMES.map((tf) => ({
                  key: tf,
                  label: tf,
                  checked: s?.tf === tf,
                  onSelect: () => chart?.setTimeframe(tf),
                })),
              })
            }
          />
        </>,
      )}
      <Sep />
      {group(
        <>
          <Button
            icon="cursor"
            label="Cursor"
            disabled={no}
            pressed={s !== undefined && s.tool === null}
            onClick={() => chart?.setTool(null)}
          />
          <Button
            icon="crosshair"
            label="Crosshair (measure)"
            disabled={no}
            pressed={s?.tool === 'measure'}
            onClick={() => pick('measure')}
          />
          {LINE_TOOLS.map(({ tool, icon }) => (
            <Button
              key={tool}
              icon={icon}
              label={TOOL_LABELS[tool]}
              disabled={no}
              pressed={s?.tool === tool}
              onClick={() => pick(tool)}
            />
          ))}
          <Button
            icon="shapes"
            label="Shapes"
            menu
            disabled={no}
            pressed={s?.tool !== undefined && s.tool !== null && SHAPES.includes(s.tool as Tool)}
            onClick={(box) =>
              setMenu({
                ...at(box),
                title: 'Shapes',
                items: SHAPES.map((t) => ({ key: t, label: TOOL_LABELS[t], onSelect: () => pick(t) })),
              })
            }
          />
          <Button
            icon="arrows"
            label={TOOL_LABELS.arrow}
            disabled={no}
            pressed={s?.tool === 'arrow'}
            onClick={() => pick('arrow')}
          />
          <Button
            icon="trend"
            label="More drawing tools"
            text="…"
            menu
            disabled={no}
            onClick={(box) =>
              setMenu({
                ...at(box),
                title: 'More drawing tools',
                items: [
                  ...MORE_TOOLS.map((t) => ({ key: t, label: TOOL_LABELS[t], onSelect: () => pick(t) })),
                  {
                    key: 'objects',
                    label: 'Objects List',
                    shortcut: 'Ctrl+B',
                    onSelect: () => chart?.openObjects(),
                  },
                ],
              })
            }
          />
        </>,
      )}
      <Sep />
      <div role="group" aria-label="Timeframe" className="flex items-center">
        {STANDARD_TIMEFRAMES.map((tf) => (
          <button
            key={tf}
            type="button"
            aria-pressed={s?.tf === tf}
            disabled={no}
            onClick={() => chart?.setTimeframe(tf)}
            className={`classic-tf ${s?.tf === tf ? 'is-pressed' : ''}`}
          >
            {tf}
          </button>
        ))}
      </div>
      <Sep />
      <Button icon="camera" label="Save as Picture" disabled={no} onClick={() => chart?.saveImage()} />
      <Button
        icon="oneClick"
        label="One Click Trading"
        shortcut="Alt+B / Alt+S"
        disabled={no}
        pressed={s?.oneClick}
        onClick={() => chart?.toggle('oneClick')}
      />
      <div className="flex-1" />
      <form
        role="search"
        className="classic-search flex items-center"
        onSubmit={(e) => {
          e.preventDefault();
          findSymbol();
        }}
      >
        <label htmlFor="classic-search" className="sr-only">
          Find a symbol
        </label>
        <input
          id="classic-search"
          list="classic-search-symbols"
          value={search}
          placeholder="Search"
          autoComplete="off"
          onChange={(e) => setSearch(e.target.value)}
        />
        <datalist id="classic-search-symbols">
          {(symbols ?? []).slice(0, 500).map((x) => (
            <option key={x.name} value={x.name}>
              {x.description}
            </option>
          ))}
        </datalist>
        <button type="submit" aria-label="Find the symbol" className="px-1">
          <ClassicIcon name="search" />
        </button>
      </form>
      {menu && (
        <ContextMenu
          x={menu.x}
          y={menu.y}
          title={menu.title}
          items={menu.items}
          searchable={menu.searchable}
          onClose={() => setMenu(null)}
        />
      )}
    </div>
  );
}
